"use client";

/**
 * OutputDocCard — the "smart HTML viewer" (founder round 08-23): renders a
 * task's output document, block by block, in the shelf's own language. The
 * model wrote MEANING (a closed alphabet: heading / row / kv / stat /
 * bullet / quote / p); this component owns every pixel — model-authored
 * HTML or markdown never exists anywhere in this path.
 *
 * The row is the workhorse: a lead slot (time / date / sender), the title,
 * quiet trailing details — one skeleton that reads as a schedule, an inbox,
 * or a file list depending only on its content.
 */

import type { OutputBlock, TaskOutputDoc } from "@bash/shared";
import { cn } from "@/lib/utils";

function Block({ block }: { block: OutputBlock }) {
  switch (block.t) {
    case "heading":
      return (
        <div className="font-mono text-2.5 font-semibold tracking-[0.13em] uppercase text-output-doc-card-ink-tint mt-2.5 mx-0 mb-1">
          {block.text}
        </div>
      );
    case "row":
      return (
        <div className="hbs-doc__row flex items-baseline gap-2.5 py-1 px-0 min-w-0 [&_+_.hbs-doc\_\_row]:[border-top:1px_dotted_color-mix(in_srgb,_var(--ink)_10%,_var(--border-subtle))]">
          {block.when && (
            <span className="flex-none font-mono text-3 font-semibold text-output-doc-card-ink-tint-2">
              {block.when}
            </span>
          )}
          <span className="flex-1 min-w-0 text-3.5 font-semibold text-fg-default whitespace-nowrap overflow-hidden text-ellipsis">
            {block.title}
          </span>
          {block.meta && (
            <span className="flex-none max-w-[42%] text-3 text-fg-subtle whitespace-nowrap overflow-hidden text-ellipsis">
              {block.meta}
            </span>
          )}
        </div>
      );
    case "kv":
      return (
        <div className="flex items-baseline gap-2 py-1 px-0">
          <span className="flex-none text-3 text-fg-muted">{block.k}</span>
          <i
            className="flex-1 [border-bottom:1px_dotted_var(--border-default)] transform-[translateY(-3px)]"
            aria-hidden="true"
          />
          <span className="flex-none max-w-[55%] text-3.5 font-semibold text-fg-default whitespace-nowrap overflow-hidden text-ellipsis">
            {block.v}
          </span>
        </div>
      );
    case "stat":
      return (
        <div className="flex items-baseline gap-2.5 pt-1 px-0 pb-0.5">
          <span className="font-gambetta text-6 leading-[1.1] [color:var(--ink,var(--hb-name))]">
            {block.value}
          </span>
          <span className="font-mono text-2.5 font-semibold tracking-[0.11em] uppercase text-fg-subtle">
            {block.label}
          </span>
        </div>
      );
    case "bullet":
      return (
        <div className="relative text-3.5 leading-normal text-fg-default pt-1 pr-0 pb-1 pl-4 after:[content:''] after:absolute after:left-0.5 after:top-[0.85em] after:w-1.5 after:h-1.5 after:rounded-full after:[background:color-mix(in_srgb,var(--ink,var(--hb-name))_60%,transparent)]">
          {block.text}
        </div>
      );
    case "quote":
      return (
        <div className="font-gambetta italic text-3.5 leading-normal text-fg-muted pt-1 pr-0 pb-1 pl-3 [border-left:2px_solid_color-mix(in_srgb,_var(--ink)_35%,_transparent)] my-1 mx-0">
          {block.text}
        </div>
      );
    case "p":
      return (
        <p className="text-3 leading-normal text-fg-muted my-1 mx-0">
          {block.text}
        </p>
      );
    default:
      return null; // an unknown future block renders as nothing, never breaks
  }
}

export function OutputDocCard({
  doc,
  headline = true,
  scroll = false,
}: {
  doc: TaskOutputDoc;
  /** The composer's sheet puts the headline on its own title line (composer panels, spec 2026-09-19); every other caller keeps it here. */
  headline?: boolean;
  /** The composer's sheet is the only copy of what it shows (often a draft waiting for their yes), so past the cap it
   *  scrolls inside the fade instead of hiding the rest; the shelf's card is a glimpse whose whole result is a tap away. */
  scroll?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex-1 min-h-0 max-h-115 [-webkit-mask-image:linear-gradient(_180deg,_#000_calc(100%_-_20px),_transparent_)] [mask-image:linear-gradient(180deg,_#000_calc(100%_-_20px),_transparent)]",
        scroll ? "overflow-y-auto overscroll-contain pb-5" : "overflow-hidden"
      )}
      data-testid="hbs-doc-body"
    >
      {headline && (
        <div className="font-gambetta text-4.5 leading-[1.3] tracking-[-0.005em] text-fg-default mb-2.5 first-letter:[color:var(--ink,var(--hb-name))]">
          {doc.headline}
        </div>
      )}
      {doc.blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}
